import React from 'react';
import {
  UserGroupIcon,
  CheckCircleIcon,
  BuildingOfficeIcon,
  DocumentTextIcon,
  PhoneIcon,
  EnvelopeIcon,
  GlobeAltIcon,
  CalendarIcon,
  BanknotesIcon,
  MagnifyingGlassIcon,
  PlusCircleIcon,
  PencilSquareIcon,
  CheckBadgeIcon,
  ReceiptPercentIcon,
  CurrencyPoundIcon,
  DocumentDuplicateIcon,
} from '@heroicons/react/24/outline';

// Add CSS for custom scrollbar
const scrollbarStyles = `
  /* Custom scrollbar styles */
  .hide-scrollbar::-webkit-scrollbar {
    height: 4px;
    width: 4px;
  }

  .hide-scrollbar::-webkit-scrollbar-track {
    background: transparent;
  }

  .hide-scrollbar::-webkit-scrollbar-thumb {
    background: #cbd5e0;
    border-radius: 10px;
  }

  .hide-scrollbar::-webkit-scrollbar-thumb:hover {
    background: #a0aec0;
  }
`;

export default function CustomerCrmHelp() {
  return (
    <div className="min-h-screen bg-cover bg-center bg-gradient-to-b from-blue-300 via-sky-100 to-white w-full">
      {/* Add custom scrollbar styles */}
      <style dangerouslySetInnerHTML={{ __html: scrollbarStyles }} />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="flex items-center justify-center mb-6">
            <div className="w-16 h-16 bg-gradient-to-r from-blue-500 to-indigo-600 rounded-2xl flex items-center justify-center shadow-lg">
              <UserGroupIcon className="h-8 w-8 text-white" />
            </div>
          </div>
          <h1 className="text-4xl font-bold text-gray-900 mb-4">Customer CRM System</h1>
          <p className="text-xl text-gray-600 max-w-4xl mx-auto leading-relaxed">
            Comprehensive customer relationship management with contact tracking, project association,
            and automated invoice generation for seamless client management.
          </p>
        </div>

        {/* System Overview */}
        <div className="bg-white rounded-3xl shadow-2xl border border-gray-200 overflow-hidden mb-12">
          <div className="bg-gradient-to-r from-blue-600 to-indigo-600 px-8 py-6">
            <div className="flex items-center">
              <UserGroupIcon className="h-8 w-8 text-white mr-4" />
              <h2 className="text-2xl font-bold text-white">Customer Management Overview</h2>
            </div>
          </div>

          <div className="p-8">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
              <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-2xl p-6 border border-blue-200">
                <div className="w-12 h-12 bg-gradient-to-r from-blue-500 to-indigo-600 rounded-xl flex items-center justify-center mb-4">
                  <BuildingOfficeIcon className="h-6 w-6 text-white" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">Client Profiles</h3>
                <p className="text-blue-700 text-sm leading-relaxed">
                  Store complete customer information including contact details, industry, and website
                  for comprehensive client tracking.
                </p>
              </div>

              <div className="bg-gradient-to-br from-indigo-50 to-purple-50 rounded-2xl p-6 border border-indigo-200">
                <div className="w-12 h-12 bg-gradient-to-r from-indigo-500 to-purple-600 rounded-xl flex items-center justify-center mb-4">
                  <DocumentDuplicateIcon className="h-6 w-6 text-white" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">Project Association</h3>
                <p className="text-indigo-700 text-sm leading-relaxed">
                  View all projects linked to each customer with expandable project details
                  and direct navigation to project management.
                </p>
              </div>

              <div className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-2xl p-6 border border-purple-200">
                <div className="w-12 h-12 bg-gradient-to-r from-purple-500 to-pink-600 rounded-xl flex items-center justify-center mb-4">
                  <ReceiptPercentIcon className="h-6 w-6 text-white" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">Invoice Generation</h3>
                <p className="text-purple-700 text-sm leading-relaxed">
                  Automatically generate detailed invoices with project materials, VAT calculations,
                  and payment links for seamless billing.
                </p>
              </div>

              <div className="bg-gradient-to-br from-pink-50 to-red-50 rounded-2xl p-6 border border-pink-200">
                <div className="w-12 h-12 bg-gradient-to-r from-pink-500 to-red-600 rounded-xl flex items-center justify-center mb-4">
                  <CalendarIcon className="h-6 w-6 text-white" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">Follow-up Tracking</h3>
                <p className="text-pink-700 text-sm leading-relaxed">
                  Schedule and track follow-ups with last contacted date and next follow-up
                  reminders to maintain client relationships.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Key Features */}
        <div className="bg-white rounded-3xl shadow-2xl border border-gray-200 overflow-hidden mb-12">
          <div className="bg-gradient-to-r from-indigo-600 to-purple-600 px-8 py-6">
            <div className="flex items-center">
              <CheckCircleIcon className="h-8 w-8 text-white mr-4" />
              <h2 className="text-2xl font-bold text-white">CRM Features & Capabilities</h2>
            </div>
          </div>

          <div className="p-8">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              <div className="bg-white rounded-xl p-6 border border-indigo-200 shadow-sm">
                <div className="flex items-center mb-4">
                  <div className="w-8 h-8 bg-indigo-500 rounded-lg flex items-center justify-center mr-3">
                    <PlusCircleIcon className="h-5 w-5 text-white" />
                  </div>
                  <h4 className="text-lg font-bold text-indigo-900">Create Customers</h4>
                </div>
                <p className="text-indigo-700 text-sm leading-relaxed">
                  Add new customers with complete contact information, industry details, source tracking,
                  and staff assignment for organized client management.
                </p>
              </div>

              <div className="bg-white rounded-xl p-6 border border-purple-200 shadow-sm">
                <div className="flex items-center mb-4">
                  <div className="w-8 h-8 bg-purple-500 rounded-lg flex items-center justify-center mr-3">
                    <PencilSquareIcon className="h-5 w-5 text-white" />
                  </div>
                  <h4 className="text-lg font-bold text-purple-900">Edit & Update</h4>
                </div>
                <p className="text-purple-700 text-sm leading-relaxed">
                  Modify customer details, update contact information, change assigned staff,
                  and toggle active status to keep records current.
                </p>
              </div>

              <div className="bg-white rounded-xl p-6 border border-pink-200 shadow-sm">
                <div className="flex items-center mb-4">
                  <div className="w-8 h-8 bg-pink-500 rounded-lg flex items-center justify-center mr-3">
                    <MagnifyingGlassIcon className="h-5 w-5 text-white" />
                  </div>
                  <h4 className="text-lg font-bold text-pink-900">Smart Search</h4>
                </div>
                <p className="text-pink-700 text-sm leading-relaxed">
                  Quickly find customers by name, contact person, email, phone, industry,
                  or any field with real-time search filtering.
                </p>
              </div>

              <div className="bg-white rounded-xl p-6 border border-blue-200 shadow-sm">
                <div className="flex items-center mb-4">
                  <div className="w-8 h-8 bg-blue-500 rounded-lg flex items-center justify-center mr-3">
                    <PhoneIcon className="h-5 w-5 text-white" />
                  </div>
                  <h4 className="text-lg font-bold text-blue-900">Contact Management</h4>
                </div>
                <p className="text-blue-700 text-sm leading-relaxed">
                  Store contact name, phone, email, address, and notes with clickable phone/email
                  links for instant communication.
                </p>
              </div>

              <div className="bg-white rounded-xl p-6 border border-emerald-200 shadow-sm">
                <div className="flex items-center mb-4">
                  <div className="w-8 h-8 bg-emerald-500 rounded-lg flex items-center justify-center mr-3">
                    <GlobeAltIcon className="h-5 w-5 text-white" />
                  </div>
                  <h4 className="text-lg font-bold text-emerald-900">Industry & Website</h4>
                </div>
                <p className="text-emerald-700 text-sm leading-relaxed">
                  Track customer industry for segmentation and store website URLs with automatic
                  protocol normalization for easy access.
                </p>
              </div>

              <div className="bg-white rounded-xl p-6 border border-yellow-200 shadow-sm">
                <div className="flex items-center mb-4">
                  <div className="w-8 h-8 bg-yellow-500 rounded-lg flex items-center justify-center mr-3">
                    <CalendarIcon className="h-5 w-5 text-white" />
                  </div>
                  <h4 className="text-lg font-bold text-yellow-900">Follow-up System</h4>
                </div>
                <p className="text-yellow-700 text-sm leading-relaxed">
                  Record last contacted date and schedule next follow-up to maintain consistent
                  communication and relationship building.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Invoice Generation */}
        <div className="bg-white rounded-3xl shadow-2xl border border-gray-200 overflow-hidden mb-12">
          <div className="bg-gradient-to-r from-emerald-600 to-teal-600 px-8 py-6">
            <div className="flex items-center">
              <ReceiptPercentIcon className="h-8 w-8 text-white mr-4" />
              <h2 className="text-2xl font-bold text-white">Automated Invoice Generation</h2>
            </div>
          </div>

          <div className="p-8">
            <div className="space-y-6">
              <div className="bg-gradient-to-r from-emerald-50 to-teal-50 rounded-xl p-6 border border-emerald-200">
                <h3 className="text-lg font-bold text-gray-900 mb-3">Project-Based Invoicing</h3>
                <p className="text-gray-700 text-sm leading-relaxed mb-4">
                  Generate professional invoices directly from customer projects. The system automatically
                  pulls project details, materials used, and calculates totals with VAT.
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="bg-white rounded-lg p-4 border border-emerald-200">
                    <h4 className="font-semibold text-emerald-900 mb-2 flex items-center">
                      <CheckBadgeIcon className="h-5 w-5 mr-2" />
                      Invoice Components
                    </h4>
                    <ul className="space-y-1 text-sm text-gray-600">
                      <li>• Project name and description</li>
                      <li>• Base project price</li>
                      <li>• Materials used (unit_cost only)</li>
                      <li>• Labor hours tracking</li>
                      <li>• VAT calculation (20% default)</li>
                      <li>• Company branding & details</li>
                    </ul>
                  </div>
                  <div className="bg-white rounded-lg p-4 border border-teal-200">
                    <h4 className="font-semibold text-teal-900 mb-2 flex items-center">
                      <BanknotesIcon className="h-5 w-5 mr-2" />
                      VAT & Payment
                    </h4>
                    <ul className="space-y-1 text-sm text-gray-600">
                      <li>• Adjustable VAT rate (default 20%)</li>
                      <li>• Customer VAT number field</li>
                      <li>• VAT deductible toggle option</li>
                      <li>• Payment link integration</li>
                      <li>• PDF download & send options</li>
                      <li>• Professional invoice formatting</li>
                    </ul>
                  </div>
                </div>
              </div>

              <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl p-6 border border-blue-200">
                <h3 className="text-lg font-bold text-gray-900 mb-3">Materials on Invoices</h3>
                <p className="text-gray-700 text-sm leading-relaxed mb-3">
                  The invoice system intelligently includes only materials marked as <strong>"used"</strong> from
                  External projects. Each material line shows name, quantity, and unit cost (not multiplied by quantity).
                </p>
                <div className="bg-white rounded-lg p-4 border border-blue-300">
                  <p className="text-blue-900 font-semibold mb-2">📋 Invoice Rules for Materials:</p>
                  <ul className="space-y-1 text-sm text-blue-800">
                    <li>✓ Only materials with <code className="bg-blue-100 px-1 rounded">used === true</code> appear</li>
                    <li>✓ Price uses <code className="bg-blue-100 px-1 rounded">unit_cost</code> field only</li>
                    <li>✓ Materials auto-load from project Materials JSON</li>
                    <li>✓ Manual material addition removed (project-sourced only)</li>
                    <li>✓ Subtotal, VAT, and Grand Total calculated automatically</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Customer Fields Reference */}
        <div className="bg-white rounded-3xl shadow-2xl border border-gray-200 overflow-hidden mb-12">
          <div className="bg-gradient-to-r from-purple-600 to-pink-600 px-8 py-6">
            <div className="flex items-center">
              <DocumentTextIcon className="h-8 w-8 text-white mr-4" />
              <h2 className="text-2xl font-bold text-white">Customer Fields Reference</h2>
            </div>
          </div>

          <div className="p-8">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-4">
                <div className="bg-blue-50 rounded-xl p-5 border border-blue-200">
                  <h4 className="font-bold text-blue-900 mb-3">Essential Information</h4>
                  <div className="space-y-3">
                    <div className="flex items-start space-x-3">
                      <BuildingOfficeIcon className="h-5 w-5 text-blue-600 mt-0.5" />
                      <div>
                        <p className="font-semibold text-sm text-gray-900">Client Name</p>
                        <p className="text-xs text-gray-600">Company or organization name</p>
                      </div>
                    </div>
                    <div className="flex items-start space-x-3">
                      <PhoneIcon className="h-5 w-5 text-blue-600 mt-0.5" />
                      <div>
                        <p className="font-semibold text-sm text-gray-900">Contact Name</p>
                        <p className="text-xs text-gray-600">Primary contact person</p>
                      </div>
                    </div>
                    <div className="flex items-start space-x-3">
                      <EnvelopeIcon className="h-5 w-5 text-blue-600 mt-0.5" />
                      <div>
                        <p className="font-semibold text-sm text-gray-900">Email & Phone</p>
                        <p className="text-xs text-gray-600">Clickable contact methods</p>
                      </div>
                    </div>
                    <div className="flex items-start space-x-3">
                      <BuildingOfficeIcon className="h-5 w-5 text-blue-600 mt-0.5" />
                      <div>
                        <p className="font-semibold text-sm text-gray-900">Address</p>
                        <p className="text-xs text-gray-600">Physical business location</p>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="bg-purple-50 rounded-xl p-5 border border-purple-200">
                  <h4 className="font-bold text-purple-900 mb-3">Business Context</h4>
                  <div className="space-y-3">
                    <div className="flex items-start space-x-3">
                      <GlobeAltIcon className="h-5 w-5 text-purple-600 mt-0.5" />
                      <div>
                        <p className="font-semibold text-sm text-gray-900">Industry</p>
                        <p className="text-xs text-gray-600">Business sector or category</p>
                      </div>
                    </div>
                    <div className="flex items-start space-x-3">
                      <GlobeAltIcon className="h-5 w-5 text-purple-600 mt-0.5" />
                      <div>
                        <p className="font-semibold text-sm text-gray-900">Website</p>
                        <p className="text-xs text-gray-600">Auto-normalized URL with https://</p>
                      </div>
                    </div>
                    <div className="flex items-start space-x-3">
                      <DocumentTextIcon className="h-5 w-5 text-purple-600 mt-0.5" />
                      <div>
                        <p className="font-semibold text-sm text-gray-900">Source</p>
                        <p className="text-xs text-gray-600">How customer was acquired</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="space-y-4">
                <div className="bg-emerald-50 rounded-xl p-5 border border-emerald-200">
                  <h4 className="font-bold text-emerald-900 mb-3">Relationship Tracking</h4>
                  <div className="space-y-3">
                    <div className="flex items-start space-x-3">
                      <CalendarIcon className="h-5 w-5 text-emerald-600 mt-0.5" />
                      <div>
                        <p className="font-semibold text-sm text-gray-900">Last Contacted</p>
                        <p className="text-xs text-gray-600">Date of most recent communication</p>
                      </div>
                    </div>
                    <div className="flex items-start space-x-3">
                      <CalendarIcon className="h-5 w-5 text-emerald-600 mt-0.5" />
                      <div>
                        <p className="font-semibold text-sm text-gray-900">Next Follow-Up</p>
                        <p className="text-xs text-gray-600">Scheduled next contact date</p>
                      </div>
                    </div>
                    <div className="flex items-start space-x-3">
                      <UserGroupIcon className="h-5 w-5 text-emerald-600 mt-0.5" />
                      <div>
                        <p className="font-semibold text-sm text-gray-900">Assigned To</p>
                        <p className="text-xs text-gray-600">Staff member managing account</p>
                      </div>
                    </div>
                    <div className="flex items-start space-x-3">
                      <CheckCircleIcon className="h-5 w-5 text-emerald-600 mt-0.5" />
                      <div>
                        <p className="font-semibold text-sm text-gray-900">Active Status</p>
                        <p className="text-xs text-gray-600">Toggle active/inactive customers</p>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="bg-yellow-50 rounded-xl p-5 border border-yellow-200">
                  <h4 className="font-bold text-yellow-900 mb-3">Notes & Documentation</h4>
                  <div className="space-y-3">
                    <div className="flex items-start space-x-3">
                      <DocumentTextIcon className="h-5 w-5 text-yellow-600 mt-0.5" />
                      <div>
                        <p className="font-semibold text-sm text-gray-900">Notes</p>
                        <p className="text-xs text-gray-600">Internal notes, preferences, history, and important details about the customer relationship</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Best Practices */}
        <div className="bg-white rounded-3xl shadow-2xl border border-gray-200 overflow-hidden">
          <div className="bg-gradient-to-r from-purple-600 to-pink-600 px-8 py-6">
            <div className="flex items-center">
              <CheckCircleIcon className="h-8 w-8 text-white mr-4" />
              <h2 className="text-2xl font-bold text-white">Customer CRM Best Practices</h2>
            </div>
          </div>

          <div className="p-8">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div className="space-y-6">
                <div className="bg-blue-50 rounded-xl p-6 border border-blue-200">
                  <h3 className="text-lg font-bold text-blue-900 mb-3">Data Management</h3>
                  <ul className="space-y-2 text-sm text-blue-700">
                    <li>• Keep contact information current and verified</li>
                    <li>• Use consistent industry categorization</li>
                    <li>• Add detailed notes after each interaction</li>
                    <li>• Verify website URLs for accuracy</li>
                    <li>• Include source to track acquisition channels</li>
                  </ul>
                </div>

                <div className="bg-indigo-50 rounded-xl p-6 border border-indigo-200">
                  <h3 className="text-lg font-bold text-indigo-900 mb-3">Follow-up Strategy</h3>
                  <ul className="space-y-2 text-sm text-indigo-700">
                    <li>• Schedule follow-ups immediately after contact</li>
                    <li>• Review next follow-up dates daily</li>
                    <li>• Update last contacted date consistently</li>
                    <li>• Set realistic follow-up intervals</li>
                    <li>• Document outcomes in notes field</li>
                  </ul>
                </div>
              </div>

              <div className="space-y-6">
                <div className="bg-purple-50 rounded-xl p-6 border border-purple-200">
                  <h3 className="text-lg font-bold text-purple-900 mb-3">Project & Invoice Flow</h3>
                  <ul className="space-y-2 text-sm text-purple-700">
                    <li>• Link projects to customers from project creation</li>
                    <li>• Mark materials "used" before invoicing</li>
                    <li>• Review invoice details before sending</li>
                    <li>• Include payment links for easy collection</li>
                    <li>• Track labor hours for accurate billing</li>
                  </ul>
                </div>

                <div className="bg-pink-50 rounded-xl p-6 border border-pink-200">
                  <h3 className="text-lg font-bold text-pink-900 mb-3">Team Coordination</h3>
                  <ul className="space-y-2 text-sm text-pink-700">
                    <li>• Assign customers to appropriate staff</li>
                    <li>• Use search to avoid duplicate entries</li>
                    <li>• Toggle inactive status for old clients</li>
                    <li>• Share customer notes with team members</li>
                    <li>• Review customer list regularly for updates</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
